import type {
  QueryClient,
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
} from "@tanstack/react-query";
import { runRead, type ReadRequest, type RetryPolicy } from "./policies";

/**
 * Who a cache entry belongs to (contract §2). Every key starts
 * `[tenant, principal, domain, …representation]`: the workspace slug, the
 * signed-in user's id, the resource, then every parameter that changes the
 * response body. Two principals in one browser never share an entry, and a
 * sign-out or account switch clears everything.
 */
export interface QueryScope {
  readonly tenant: string;
  readonly principal: string;
}

/** A protected read was asked for before the session said who is signed in. */
export class ScopeNotReadyError extends Error {
  constructor() {
    super("No signed-in principal: a scoped query must wait for the session");
    this.name = "ScopeNotReadyError";
  }
}

/**
 * A read settled after the principal it was made for signed out or was
 * replaced. Its answer is dropped, whether or not the service could abort.
 */
export class ScopeChangedError extends Error {
  constructor() {
    super("The session changed while this read was in flight; its answer was dropped");
    this.name = "ScopeChangedError";
  }
}

let principal: string | null = null;
/** Advances on every principal change; a read compares it before and after. */
let epoch = 0;

/** The scope for `tenant` under the signed-in principal. */
export function scopeFor(tenant: string): QueryScope {
  if (principal === null) throw new ScopeNotReadyError();
  return { tenant, principal };
}

/**
 * The scope for `tenant`, or `null` before the session has said who is signed
 * in: a route loader on a cold load runs before the session resolves. The
 * caller then reads straight from its service, uncached, so the page still
 * gets its data (the cold-load check); it never waits on, or skips, the read.
 */
export function scopeIfReady(tenant: string, what: string): QueryScope | null {
  try {
    return scopeFor(tenant);
  } catch (error) {
    if (!(error instanceof ScopeNotReadyError)) throw error;
    console.warn(`[queries] ${what} read before the session resolved; not cached`, { tenant });
    return null;
  }
}

/**
 * The tenant for a read no workspace owns: the currency, time zone and OAuth
 * scope lists. Not a possible workspace slug, so it never shares a key with
 * one; the principal still leads the key, so a sign-out clears it like any
 * other entry.
 */
export const GLOBAL_TENANT = "*";

/** A key under `scope`. The representation must hold plain JSON values only. */
export function scopedKey<const R extends readonly unknown[]>(
  scope: QueryScope,
  domain: string,
  ...representation: R
): readonly [string, string, string, ...R] {
  return [scope.tenant, scope.principal, domain, ...representation];
}

/**
 * Tell the cache who is signed in: the user's id once the session resolves,
 * `null` on sign-out or when the session is found gone. A change of principal
 * (sign-out, account switch, session expiry) advances the epoch, cancels every
 * read in flight, and clears the query and mutation caches. A repeat of the
 * current principal does nothing, so the session re-reads after a profile edit
 * cost nothing.
 *
 * `client` is the app's client if one exists (`peekQueryClient()`); passing
 * `null` still advances the epoch, so a read already in flight is dropped.
 */
export function syncQueryPrincipal(next: string | null, client: QueryClient | null): void {
  if (next === principal) return;
  principal = next;
  epoch += 1;
  if (!client) return;
  client.cancelQueries().catch((error: unknown) => {
    console.warn("[queries] cancelling reads on a session change failed", error);
  });
  client.clear();
}

/**
 * The query function for a scoped read. It forwards Query's abort signal to
 * the service (`ReadRequest`), runs the retry policy (only `"safe-read"`
 * retries, see `policies.ts`), and drops an answer that lands after the
 * principal changed: Axios aborts on cancel, but not every service can, and a
 * late answer must not reach the next user's screen. `deadlineMs` lengthens
 * the read's budget past the interactive deadline, for a page walk only
 * (contract §6.2).
 */
export function scopedRead<T, TPageParam = never>(
  read: (request: ReadRequest, context: QueryFunctionContext<QueryKey, TPageParam>) => Promise<T>,
  { retry = "none", deadlineMs }: { retry?: RetryPolicy; deadlineMs?: number } = {},
): QueryFunction<T, QueryKey, TPageParam> {
  return async (context) => {
    const startedIn = epoch;
    const result = await runRead(
      (request) => read(request, context),
      context.signal,
      retry,
      deadlineMs,
    );
    if (epoch !== startedIn) throw new ScopeChangedError();
    return result;
  };
}

/** Reset module state between tests. */
export function resetQueryScopeForTesting(): void {
  principal = null;
  epoch = 0;
}
